'use client';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { HtmlDescription } from './shared/html-description';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { PiCards, PiStudent } from 'react-icons/pi';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { GrDocumentUser, GrUserWorker } from 'react-icons/gr';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { getSlugFromPage } from '@eliadmin/core/src/utils/url-utils';
import { MdCastForEducation } from 'react-icons/md';
import type { TDynamicData } from '../../types/dynamic-data';
import { useQuery } from '@tanstack/react-query';
import { fetchDynamicSectionsById } from '@eliadmin/core/src/api/dynamics/dynamics-api';
import { attachHeroParallax } from './hero-parallax';
import { homepageHeadingKeys } from './homepage-heading-utils';

// Floating Image Component with Skeleton
const FloatingImage = ({
  src,
  alt,
  className,
  style,
  width,
  height,
}: {
  src: string;
  alt: string;
  className: string;
  style: any;
  width: number;
  height: number;
}) => {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return <Skeleton className={cn(className, 'bg-gray-200')} style={style} />;
  }

  return (
    <Image
      src={src}
      alt={alt}
      width={width}
      height={height}
      quality={75}
      sizes="(min-width: 768px) 225px, 113px"
      className={cn(className, 'bg-gray-200')}
      style={style}
      onError={() => {
        setHasError(true);
      }}
    />
  );
};

export function Hero({ description }: { image: string; title: string; description: string }) {
  const me = useCustomerStore(state => state.me);
  const heroRef = useRef<HTMLDivElement>(null);

  const { data: images } = useQuery({
    queryKey: ['HOMEPAGE_IMAGES', 'sk', 7],
    queryFn: () => fetchDynamicSectionsById<TDynamicData>({ locale: 'sk', id: 7 }),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (heroRef.current) return attachHeroParallax(heroRef.current, window);
  }, []);

  const { t } = useTranslations();

  return (
    <section
      ref={heroRef}
      className={cn(
        'custom-section overflow-hidden',
        'gap-24 bg-[#f3f3f3] pb-8 pt-20 md:pt-[200px] xl:pb-5',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'relative z-[5] flex flex-col items-center justify-center gap-8',
        )}
      >
        <h1 className="w-full max-w-[800px] text-center text-4xl font-bold text-black sm:text-5xl lg:text-6xl">
          <span className="rounded-lg bg-[#3477F51A] px-6 text-[#3477F5]">{t(homepageHeadingKeys[0])}</span>
          <br />
          {t(homepageHeadingKeys[1])}
          <br />
          <span className="rounded-lg bg-[#3477F51A] px-6 text-[#3477F5]">{t(homepageHeadingKeys[2])}</span>
        </h1>
        <HtmlDescription
          content={description}
          color="white"
          size="lg"
          className="w-full max-w-[800px] rounded-lg bg-white/60 p-2 text-center font-medium !text-black xl:bg-transparent xl:p-0"
        />
        <div className="flex w-full flex-col items-center justify-center gap-4 sm:flex-row">
          <Button
            asChild
            variant={'secondary'}
            className="w-full border border-[#D8D8DB] sm:w-[240px]"
          >
            <Link prefetch={true} href={me ? `${ROUTES.PROFILE()}` : `${ROUTES.REGISTER()}`}>
              {t('text2122')}
            </Link>
          </Button>
          <Button asChild className="w-full sm:w-[240px]">
            <Link
              prefetch={true}
              href={me ? `${ROUTES.JOB_OFFERS()}` : `${ROUTES.REGISTER()}?type=jobseeker`}
            >
              {t('text2123')}
            </Link>
          </Button>
        </div>
        <div
          className={cn(
            'custom-container',
            'relative z-[5] grid grid-cols-1 justify-center gap-4 text-black sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5',
          )}
        >
          <Link
            href={getSlugFromPage('curriculum-vitaes')}
            prefetch={true}
            className="  col-span-1 flex h-[72px] items-center justify-start gap-2 rounded-full bg-white"
          >
            <GrDocumentUser className="ml-6 min-h-7 max-w-7 flex-1 pb-[1px]" />
            <p className="">{t('text1739')}</p>
          </Link>
          <Link
            href={ROUTES.JOB_OFFERS()}
            prefetch={true}
            className=" col-span-1 flex h-[72px] items-center justify-start gap-2 rounded-full bg-white"
          >
            <GrUserWorker className="ml-6 h-7 max-w-7 flex-1" />
            <p className="">{t('text788')}</p>
          </Link>
          <Link
            href={ROUTES.DUAL_EDUCATION()}
            prefetchOnHover
            className="  col-span-1 flex h-[72px] items-center justify-start gap-2 rounded-full bg-white"
          >
            <MdCastForEducation className="ml-6 h-8 max-w-8 flex-1" />
            <p className="">Duálne vzdelávanie</p>
          </Link>
          <Link
            href={getSlugFromPage('courses')}
            prefetchOnHover
            className=" col-span-1 flex h-[72px] items-center justify-start gap-2 rounded-full bg-white"
          >
            <PiStudent className="ml-6 h-8 max-w-8 flex-1" />
            <p className="">{t('text2120')}</p>
          </Link>
          <Link
            href={ROUTES.BLOG()}
            prefetchOnHover
            className="  col-span-1 flex h-[72px] items-center justify-start gap-2 rounded-full bg-white"
          >
            <PiCards className="ml-6 h-8 max-w-8 flex-1" />
            <p className="">{t('text161')}</p>
          </Link>
        </div>
      </div>

      {/* Floating Images */}
      <div className="absolute inset-0 z-[1]">
        {/* Left Side Images (2 images) */}
        <FloatingImage
          src={images?.[0]?.image?.url ?? ''}
          alt="Floating Image 1"
          width={225}
          height={300}
          className="absolute -left-[5%] top-[20%] h-[150px] w-[112.5px] object-cover md:left-[10%] md:top-[40%] md:h-[300px] md:w-[225px]"
          style={{
            transform: 'translateY(var(--hero-parallax-y, 0px))',
          }}
        />
        <FloatingImage
          src={images?.[1]?.image?.url ?? ''}
          alt="Floating Image 2"
          width={225}
          height={300}
          className="absolute -left-[8%] top-[40%] h-[150px] w-[112.5px] object-cover md:left-[5%] md:top-[70%] md:h-[300px] md:w-[225px]"
          style={{
            transform: 'translateY(var(--hero-parallax-y, 0px))',
          }}
        />

        {/* Right Side Images (3 images) */}
        <FloatingImage
          src={images?.[4]?.image?.url ?? ''}
          alt="Floating Image 3"
          width={225}
          height={300}
          className="absolute -right-[5%] top-[10%] h-[150px] w-[112.5px] object-cover md:right-[10%] md:top-[30%] md:h-[300px] md:w-[225px]"
          style={{
            transform: 'translateY(var(--hero-parallax-y, 0px))',
          }}
        />
        <FloatingImage
          src={images?.[3]?.image?.url ?? ''}
          alt="Floating Image 4"
          width={225}
          height={300}
          className="absolute -right-[8%] top-[30%] h-[150px] w-[112.5px] object-cover md:right-[5%] md:top-[60%] md:h-[300px] md:w-[225px]"
          style={{
            transform: 'translateY(var(--hero-parallax-y, 0px))',
          }}
        />
        <FloatingImage
          src={images?.[2]?.image?.url ?? ''}
          alt="Floating Image 5"
          width={225}
          height={300}
          className="absolute right-[0%] top-[60%] h-[150px] w-[112.5px] object-cover md:right-[15%] md:top-[90%] md:h-[300px] md:w-[225px]"
          style={{
            transform: 'translateY(var(--hero-parallax-y, 0px))',
          }}
        />
      </div>

      {/* Background Image */}
      <Image
        src={'/images/banner-lines.png'}
        alt=""
        fill
        priority
        fetchPriority="high"
        sizes="100vw"
        style={{ objectFit: 'cover', objectPosition: 'center' }}
        className="absolute inset-0 z-[2] h-full w-full"
      />
    </section>
  );
}
